import {
  ContentBundleSchema,
  IngestResultSchema,
  PROTOCOL_VERSION,
  type IngestResult,
  type TalentEvent,
} from "@talent/protocol";
import type { Transport } from "@talent/client-telemetry";
import { z } from "zod";

/**
 * Server transport.
 *
 * The one rule that matters here: this module NEVER decides which content bundle to run. It asks.
 * The app updates without user action, so a client that chose "latest" for itself could move a
 * child across a content change mid-year with no trace in the data (ADR-002, v2 §18).
 */

const BASE = import.meta.env["VITE_API_BASE"] ?? "";

/**
 * Responses are PARSED, never cast.
 *
 * A cast is a claim about the server that the client cannot check, and this client acts on what it
 * receives: it runs a task from a bundle definition. A malformed bundle should fail here, with a
 * message naming the field, rather than as an undefined read halfway through a trial that a child
 * then has to sit through.
 */
const BundleAssignmentResponseSchema = z.object({
  profileId: z.string().uuid(),
  bundleVersion: z.string().min(1),
  reEquatedFrom: z.string().nullable(),
  reEquatingNote: z.string().nullable(),
  bundle: ContentBundleSchema,
});
export type BundleAssignmentResponse = z.infer<typeof BundleAssignmentResponseSchema>;

/** Nobody is signed in, or the session was revoked or has expired. */
export class UnauthenticatedError extends Error {
  constructor() {
    super("sign in required");
    this.name = "UnauthenticatedError";
  }
}

/** Signed in as someone this data does not belong to. */
export class ForbiddenError extends Error {
  constructor() {
    super("not permitted");
    this.name = "ForbiddenError";
  }
}

/**
 * The bundle a student is pinned to, with its tasks and objectives.
 *
 * Authenticated, unlike its predecessor: there is no unclaimed profile any more, so there is no
 * caller who needs this without a session (ADR-011).
 */
export async function fetchAssignedBundle(
  profileId: string,
): Promise<BundleAssignmentResponse> {
  const res = await fetch(`${BASE}/v1/students/${profileId}/bundle`, {
    headers: { accept: "application/json" },
    credentials: "same-origin",
  });
  if (res.status === 401) throw new UnauthenticatedError();
  if (res.status === 403) throw new ForbiddenError();
  if (!res.ok) throw new Error(`bundle assignment failed: ${res.status}`);
  return BundleAssignmentResponseSchema.parse(await res.json());
}

/**
 * Ingest transport. Throws on any non-2xx or network failure so the outbox keeps the batch queued
 * and retries — silently swallowing a failure here would make the client believe events were
 * durable when they were not (Phase 0 R9, irreversible).
 *
 * **Carries the student's own session, and nothing else** (ADR-011). It used to carry a device
 * write grant, because a child had no account and ingest could not be gated on a session. Now that
 * a student signs in there is one credential instead of two, and it is the cookie the browser
 * attaches — so a tablet that has been signed out cannot append to a child's record.
 */
export class HttpTransport implements Transport {
  async send(events: TalentEvent[]): Promise<IngestResult> {
    const res = await fetch(`${BASE}/v1/events`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ protocolVersion: PROTOCOL_VERSION, events }),
      // Let the browser deliver a final batch during page teardown.
      keepalive: events.length <= 20,
    });
    if (!res.ok) throw new Error(`ingest failed: ${res.status}`);
    return IngestResultSchema.parse(await res.json());
  }
}

/**
 * A transport that accepts everything and sends nothing.
 *
 * For the teacher's game preview. A teacher's session cannot append events — the server refuses it,
 * because a teacher is not the instrument (R-3) — so the alternative to discarding here is a recorder
 * that retries a batch the server will keep refusing, forever, out of an outbox that never drains.
 *
 * **The renderers are unchanged by this**, which is the point: a previewed game is the real game, with
 * the real trial loop and the real difficulty ladder. Making the recorder optional instead would have
 * put an `if` around every `record` call in twelve files, and the first one anybody forgot would be a
 * crash in a game rather than a discarded event.
 *
 * Paired with `MemoryOutboxStore`, so nothing touches IndexedDB either and a preview leaves no trace
 * on the device.
 */
export class DiscardingTransport implements Transport {
  async send(events: TalentEvent[]): Promise<IngestResult> {
    // Reported as accepted, because from the recorder's point of view they are dealt with. Saying
    // "rejected" would make it retry them.
    return { accepted: events.length, duplicates: 0, rejected: [] };
  }
}
